import type { MutableRefObject } from 'react';
import type { EntityTableAdapter } from './EntityTableAdapter';
import type { TableColumn } from '../GenericTable';
import type { Person } from '../../../types/Person';
import { mapScimUserToPerson } from '../../../types/Person';
import type { EntityType } from '../../../types/ScimUser';
import type { ScimEntityService } from '../../../services/ScimEntityService';
import { scimPersonService } from '../../../services/ScimPersonService';
import { extractListLevelDollarUnderscoreProps } from '../../../utils/listResponseMeta';
import { combineScimFiltersWithAnd } from '../../../utils/simpleSearchUtils';
import { getDefaultRequestedAttributesForEntityType } from '../../../utils/tableAttributesUtils';

function getRequestedAttributesForColumns(cols: Array<TableColumn<Person>>): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    const add = (raw: unknown) => {
        const s = (raw ?? '').toString().trim();
        if (!s) return;
        const normalized = s.replace(/^\$\./, '');
        const key = normalized.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(normalized);
    };
    add('Id');
    add('DisplayName');
    for (const c of cols) add(c.title ?? c.key);
    return out;
}

export function createPersonManagementTableAdapter(args: {
    effectiveEntityTypeForHook: string;
    selectedEntityType?: EntityType;
    itemsPerPage: number;
    entityService: ScimEntityService;
    allowedTableAttributeIdsRef: MutableRefObject<string[] | null | undefined>;
    getFilteredColumns: () => TableColumn<Person>[];
}): EntityTableAdapter<Person> {
    const {
        effectiveEntityTypeForHook,
        selectedEntityType,
        itemsPerPage,
        entityService,
        allowedTableAttributeIdsRef,
        getFilteredColumns
    } = args;

    return {
        entityType: effectiveEntityTypeForHook,
        getColumns: () => getFilteredColumns(),
        loadData: async (
            page,
            abortSignal,
            searchFilter,
            _selectedType,
            sortBy,
            searchtext,
            searchAttributes,
            omitSearchDialectExpression
        ) => {
            const entityTypeToUse = effectiveEntityTypeForHook === 'person' ? null : effectiveEntityTypeForHook;

            if (entityTypeToUse) {
                const allowed = allowedTableAttributeIdsRef.current;
                const baseColumnsForView = getDefaultRequestedAttributesForEntityType(entityTypeToUse);

                const baseRequested = [...new Set(baseColumnsForView.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '')).filter(Boolean))];
                const allowedLower = Array.isArray(allowed)
                    ? new Set(allowed.map((a) => (a ?? '').toString().trim().replace(/^\$\./, '').toLowerCase()).filter(Boolean))
                    : null;
                const attributes: string[] =
                    allowedLower
                        ? baseRequested.filter((a) => {
                            const k = a.toLowerCase();
                            return k === 'id' || k === 'displayname' || allowedLower.has(k);
                        })
                        : baseRequested;

                let effectiveFilter = searchFilter;
                let effectiveSearchText = searchtext;
                if (selectedEntityType === 'Applicant' && effectiveSearchText) {
                    const q = effectiveSearchText;
                    effectiveFilter = combineScimFiltersWithAnd(
                        effectiveFilter,
                        `DisplayName co "${q}" or UserPrincipalName co "${q}" or JobCode co "${q}" or Id co "${q}"`
                    );
                    effectiveSearchText = undefined;
                }

                const hasPhotoAttr = attributes.some(a => a.toLowerCase() === 'photo');
                const resp = await entityService.getEntitiesByType(
                    entityTypeToUse.toLowerCase(),
                    page,
                    itemsPerPage,
                    sortBy,
                    effectiveFilter || undefined,
                    attributes,
                    abortSignal,
                    effectiveSearchText,
                    searchAttributes,
                    undefined,
                    undefined,
                    omitSearchDialectExpression,
                    hasPhotoAttr ? false : true
                );

                const listMeta = extractListLevelDollarUnderscoreProps(resp);
                return {
                    ...listMeta,
                    data: (resp.data || []).map((entity: any) => mapScimUserToPerson(entity)),
                    totalResults: resp.totalResults || 0,
                    currentPage: resp.currentPage || page,
                    totalPages: resp.totalPages || 1,
                    hasNextPage: resp.hasNextPage || false,
                    hasPreviousPage: resp.hasPreviousPage || false
                };
            }

            const attrs = getRequestedAttributesForColumns(getFilteredColumns());
            if (!attrs.some(a => a.toLowerCase() === 'photo')) attrs.push('Photo');
            const personsResponse = await scimPersonService.searchPersons({
                page,
                pageSize: itemsPerPage,
                sortBy,
                filter: searchFilter,
                searchtext,
                searchAttributes,
                omitSearchDialectExpression,
                attributes: attrs,
                skipReferenceExpansion: false
            }, abortSignal);

            const rows = personsResponse.Resources || [];
            const totalResults = personsResponse.totalResults || 0;
            const totalPages = Math.max(1, Math.ceil(totalResults / itemsPerPage));
            const listMeta = extractListLevelDollarUnderscoreProps(personsResponse);
            return {
                ...listMeta,
                data: rows,
                totalResults,
                currentPage: page,
                totalPages,
                hasNextPage: page < totalPages,
                hasPreviousPage: page > 1
            };
        },
        deleteById: async (id: string) => {
            const lower = effectiveEntityTypeForHook === 'person' ? 'person' : effectiveEntityTypeForHook;
            await entityService.deleteEntity(lower, id);
        }
    };
}
